import { HYDRATE } from 'next-redux-wrapper';
import {
    createAction,
    createAsyncThunk,
    createSlice,
    isAnyOf,
} from '@reduxjs/toolkit';
import { deleteReq, getReq, patchReq, postReq } from 'api';
import { profilePaths, revalidatePaths } from 'utils/revalidate';

const hydrate = createAction(HYDRATE);

// The publisher's excerpt is no longer displayed, so it must not travel to the
// browser either: SSR props and the hydrated store are both public page source.
// The headline is still rendered on the card, so it stays. The admin
// add-coverage preview reads `state.coverage` from the metascraper endpoint, so
// it is unaffected either way.
const withoutPublisherExcerpt = ({ description, ...coverage }) => coverage;

// Accepts a full alert URL, a path, or a bare slug — whatever an admin happens to
// paste. The slug is the last path segment, so query strings and trailing slashes
// both drop out.
const alertSlugFromLink = (link) =>
    String(link || '')
        .trim()
        .split(/[?#]/)[0]
        .split('/')
        .filter(Boolean)
        .pop() || '';

export const fetchCoverages = createAsyncThunk(
    'company/fetchCoverages',
    async ({ id, size } = {}, { getState, rejectWithValue }) => {
        try {
            const res = await getReq(
                `/profiles/${getState().company.data.id || id}/coverages${
                    size ? `?size=${size}` : ''
                }`
            );
            return {
                ...res.data.data,
                items: (res.data.data.items || []).map(withoutPublisherExcerpt),
            };
        } catch (err) {
            let error = err;
            if (!error.response) {
                throw err;
            }
            return rejectWithValue(error.response.data);
        }
    }
);

// Options for the admin add-coverage alert picker: the alerts this profile has
// actually responded to.
export const fetchRespondedAlerts = createAsyncThunk(
    'company/fetchRespondedAlerts',
    async (_, { getState, rejectWithValue }) => {
        try {
            const res = await getReq(
                `/profiles/${getState().company.data.id}/alerts-responded`
            );
            return res.data.data;
        } catch (err) {
            let error = err;
            if (!error.response) {
                throw err;
            }
            return rejectWithValue(error.response.data);
        }
    }
);

// The picker only lists alerts this profile answered, so a story that came from any
// other alert — or from the archive — has no option to select. Admins paste its link
// instead, and we resolve it to the alert the same page URL would open.
export const resolveAlertByLink = createAsyncThunk(
    'company/resolveAlertByLink',
    async (link, { rejectWithValue }) => {
        const slug = alertSlugFromLink(link);
        if (!slug) {
            return rejectWithValue({ message: 'Enter a News Alert link.' });
        }
        try {
            const res = await getReq(
                `/alerts/slug/${encodeURIComponent(slug)}`
            );
            return res.data.data;
        } catch (err) {
            let error = err;
            if (!error.response) {
                throw err;
            }
            return rejectWithValue(error.response.data);
        }
    }
);

export const addCoverage = createAsyncThunk(
    'company/addCoverage',
    async (coverageDto, { getState, rejectWithValue }) => {
        try {
            const res = await postReq(
                `/profiles/${getState().company.data.id}/coverages`,
                coverageDto
            );
            revalidatePaths(profilePaths(getState().company.data.slug));
            return withoutPublisherExcerpt(res.data.data);
        } catch (err) {
            let error = err;
            if (!error.response) {
                throw err;
            }
            return rejectWithValue(error.response.data);
        }
    }
);

// Corrects a logged piece in place; the API sends no e-mail for it.
export const updateCoverage = createAsyncThunk(
    'company/updateCoverage',
    async ({ id, ...coverageDto }, { getState, rejectWithValue }) => {
        try {
            const res = await patchReq(
                `/profiles/${getState().company.data.id}/coverages/${id}`,
                coverageDto
            );
            revalidatePaths(profilePaths(getState().company.data.slug));
            return withoutPublisherExcerpt(res.data.data);
        } catch (err) {
            let error = err;
            if (!error.response) {
                throw err;
            }
            return rejectWithValue(error.response.data);
        }
    }
);

export const removeCoverage = createAsyncThunk(
    'company/removeCoverage',
    async (id, { getState, rejectWithValue }) => {
        try {
            await deleteReq(
                `/profiles/${getState().company.data.id}/coverages/${id}`
            );
            revalidatePaths(profilePaths(getState().company.data.slug));
            return id;
        } catch (err) {
            let error = err;
            if (!error.response) {
                throw err;
            }
            return rejectWithValue(error.response.data);
        }
    }
);

export const checkCoverage = createAsyncThunk(
    'company/checkCoverage',
    async (url, { rejectWithValue }) => {
        try {
            const res = await postReq(`/coverages/fetch-coverage`, { url });
            return res.data.data;
        } catch (err) {
            let error = err;
            if (!error.response) {
                throw err;
            }
            return rejectWithValue(error.response.data);
        }
    }
);

export const pinCoverage = createAsyncThunk(
    'company/pinCoverage',
    async (id, { getState, rejectWithValue }) => {
        try {
            const res = await patchReq(
                `/profiles/${getState().company.data.id}/coverages/${id}/pin`
            );
            revalidatePaths(profilePaths(getState().company.data.slug));
            return withoutPublisherExcerpt(res.data.data);
        } catch (err) {
            let error = err;
            if (!error.response) {
                throw err;
            }
            return rejectWithValue(error.response.data);
        }
    }
);

export const seeCoverages = createAsyncThunk(
    'company/seeCoverages',
    async (id, { getState, rejectWithValue }) => {
        try {
            const res = await patchReq(
                `/profiles/${getState().company.data.id}/coverages/see`
            );
            return res.data.data;
        } catch (err) {
            let error = err;
            if (!error.response) {
                throw err;
            }
            return rejectWithValue(error.response.data);
        }
    }
);

const initialState = {
    data: null,
    total: 0,
    loading: false,
    error: null,
    coverage: null,
    respondedAlerts: [],
    editing: null,
};

const coveragesSlice = createSlice({
    name: 'coverages',
    initialState,
    reducers: {
        reset: () => initialState,
        editCoverage: (state, { payload }) => {
            state.editing = payload;
        },
    },
    extraReducers(builder) {
        builder
            .addCase(hydrate, (state, action) => {
                return {
                    ...state,
                    ...action.payload.company.coverages,
                };
            })
            .addCase(fetchCoverages.fulfilled, (state, { payload }) => {
                state.data = payload.items;
                state.total = payload.totalItems;
                state.coverage = null;
            })
            .addCase(addCoverage.fulfilled, (state, { payload }) => {
                // The API returns the existing row when the article was already
                // logged on this profile.
                state.data = [
                    payload,
                    ...state.data.filter((item) => item.id !== payload.id),
                ];
            })
            .addCase(updateCoverage.fulfilled, (state, { payload }) => {
                state.data = state.data.map((item) =>
                    item.id === payload.id ? { ...item, ...payload } : item
                );
                state.editing = null;
            })
            .addCase(removeCoverage.fulfilled, (state, { payload }) => {
                state.data = state.data.filter((item) => item.id !== payload);
            })
            .addCase(checkCoverage.fulfilled, (state, { payload }) => {
                state.coverage = payload;
            })
            .addCase(fetchRespondedAlerts.fulfilled, (state, { payload }) => {
                state.respondedAlerts = payload || [];
            })
            .addCase(fetchRespondedAlerts.rejected, (state) => {
                state.respondedAlerts = [];
            })
            .addCase(pinCoverage.fulfilled, (state, { payload }) => {
                state.data = state.data.map((item) => {
                    if (item.id === payload.id) {
                        return payload;
                    }
                    return item;
                });
            })
            .addMatcher(
                isAnyOf(
                    fetchCoverages.fulfilled,
                    addCoverage.fulfilled,
                    removeCoverage.fulfilled,
                    checkCoverage.fulfilled
                ),
                (state) => {
                    state.loading = false;
                    state.error = null;
                }
            )
            .addMatcher(
                isAnyOf(
                    fetchCoverages.pending,
                    addCoverage.pending,
                    removeCoverage.pending,
                    checkCoverage.pending
                ),
                (state) => {
                    state.loading = true;
                    state.error = null;
                }
            )
            .addMatcher(
                isAnyOf(
                    fetchCoverages.rejected,
                    addCoverage.rejected,
                    removeCoverage.rejected,
                    checkCoverage.rejected
                ),
                (state, { payload }) => {
                    state.loading = false;
                    state.coverage = null;
                    state.error = payload;
                }
            );
    },
});

export const { reset, editCoverage } = coveragesSlice.actions;

export const coveragesSelector = ({ company }) => company.coverages;

export default coveragesSlice.reducer;
